import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Languages, Sparkles, CheckCircle2, RotateCcw, Loader2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import DoctorShell from "@/components/DoctorShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";

/**
 * Phase 3(b) — Multilingual public profile (EN / HI / MR).
 *
 * Doctor:
 *  1. Clicks "Generate" — LLM drafts translations for biography, summary,
 *     and specialization description.
 *  2. Edits fields inline.
 *  3. Clicks "Approve" — the translation goes live on the public page and
 *     the language pill appears in the switcher there.
 *
 * Only APPROVED languages appear on the public page (EN is always shown).
 * hreflang tags are injected server-side alongside a floating switcher.
 */

const FIELD_META = [
    { key: "professional_biography", label: "Professional biography", rows: 8, max: 6000 },
    { key: "profile_summary", label: "Profile summary", rows: 3, max: 2000 },
    { key: "specialization_description", label: "Specialization description", rows: 4, max: 3000 },
];

function FaqEditor({ faqs, onChange, disabled }) {
    const list = Array.isArray(faqs) ? faqs : [];
    if (!list.length) {
        return (
            <p className="text-xs text-[#17212B]/60">
                No FAQs in the translation yet. Click Generate above to draft FAQs from the published EN list.
            </p>
        );
    }
    const setItem = (i, key, v) => {
        const next = list.map((item, idx) => (idx === i ? { ...item, [key]: v } : item));
        onChange(next);
    };
    return (
        <div className="space-y-3">
            {list.map((f, i) => (
                <div key={i} className="border border-[#123B5D]/10 rounded-md p-3 space-y-2" data-testid={`translations-faq-item-${i}`}>
                    <div className="text-[10px] uppercase tracking-widest text-[#17212B]/50">FAQ {i + 1}</div>
                    <input
                        className="w-full px-3 py-2 text-sm border rounded-md"
                        value={f.q || ""}
                        onChange={(e) => setItem(i, "q", e.target.value)}
                        placeholder="Question"
                        disabled={disabled}
                        data-testid={`translations-faq-q-${i}`}
                    />
                    <textarea
                        rows={3}
                        className="w-full px-3 py-2 text-sm border rounded-md"
                        value={f.a || ""}
                        onChange={(e) => setItem(i, "a", e.target.value)}
                        placeholder="Answer"
                        disabled={disabled}
                        data-testid={`translations-faq-a-${i}`}
                    />
                </div>
            ))}
        </div>
    );
}

export default function DoctorTranslations() {
    const [meta, setMeta] = useState(null);
    const [activeLang, setActiveLang] = useState("hi");
    const [row, setRow] = useState(null);
    const [drafts, setDrafts] = useState({});
    const [dirty, setDirty] = useState(false);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [saving, setSaving] = useState(false);
    const [approving, setApproving] = useState(false);

    const load = useCallback(async () => {
        try {
            const [m, r] = await Promise.all([
                api.get("/me/translations"),
                api.get(`/me/translations/${activeLang}`),
            ]);
            setMeta(m.data);
            setRow(r.data);
            setDrafts({ ...(r.data?.fields || {}) });
            setDirty(false);
        } catch (e) {
            toast.error(errMsg(e, "Failed to load translations"));
        } finally {
            setLoading(false);
        }
    }, [activeLang]);

    useEffect(() => { load(); }, [load]);

    const onField = (k) => (e) => {
        setDrafts({ ...drafts, [k]: e.target.value });
        setDirty(true);
    };

    async function generate() {
        setGenerating(true);
        try {
            const r = await api.post(`/me/translations/${activeLang}/generate`);
            setRow(r.data);
            setDrafts({ ...(r.data?.fields || {}) });
            setDirty(false);
            toast.success("AI drafts ready — review each field before approving.");
        } catch (e) {
            toast.error(errMsg(e, "Generation failed"));
        } finally {
            setGenerating(false);
        }
    }

    async function saveDraft() {
        setSaving(true);
        try {
            const r = await api.put(`/me/translations/${activeLang}`, { fields: drafts });
            setRow(r.data);
            setDirty(false);
            toast.success("Draft saved.");
        } catch (e) {
            toast.error(errMsg(e, "Save failed"));
        } finally {
            setSaving(false);
        }
    }

    async function approve() {
        if (dirty) await saveDraft();
        setApproving(true);
        try {
            const r = await api.post(`/me/translations/${activeLang}/approve`);
            setRow(r.data);
            toast.success(`${row?.lang?.toUpperCase()} translation approved — live on your public page.`);
            load();
        } catch (e) {
            toast.error(errMsg(e, "Approve failed"));
        } finally {
            setApproving(false);
        }
    }

    async function unapprove() {
        setApproving(true);
        try {
            const r = await api.post(`/me/translations/${activeLang}/unapprove`);
            setRow(r.data);
            toast.info("Translation moved back to draft.");
        } catch (e) {
            toast.error(errMsg(e, "Unapprove failed"));
        } finally {
            setApproving(false);
        }
    }

    if (loading) return <DoctorShell><div className="text-[#17212B]/60">Loading translations…</div></DoctorShell>;
    if (!meta || !row) return <DoctorShell><div>Not found.</div></DoctorShell>;

    const langs = meta.supported_langs || { hi: "Hindi", mr: "Marathi" };
    const isApproved = row.status === "approved";

    return (
        <DoctorShell>
            <div className="mb-6" data-testid="translations-header">
                <div className="text-xs uppercase tracking-widest text-[#0F766E] font-semibold">Public Profile Translations</div>
                <h1 className="text-2xl font-bold text-[#123B5D] flex items-center gap-2">
                    <Languages size={20} /> EN · HI · MR
                </h1>
                <p className="text-sm text-[#17212B]/60 mt-1">
                    English is your source of truth. Draft translations here, review each field, then approve — only APPROVED languages appear
                    in the public language switcher (with proper <code>hreflang</code> tags).
                </p>
            </div>

            {/* Lang tabs */}
            <div className="flex gap-2 mb-4" data-testid="translations-lang-tabs">
                {Object.entries(langs).map(([code, name]) => (
                    <button
                        key={code}
                        onClick={() => setActiveLang(code)}
                        className={`px-4 py-2 rounded-full text-sm font-semibold border ${
                            activeLang === code
                                ? "bg-[#123B5D] text-white border-[#123B5D]"
                                : "bg-white text-[#123B5D] border-[#123B5D]/30 hover:border-[#123B5D]"
                        }`}
                        data-testid={`translations-lang-${code}`}
                    >
                        {name} · {code.toUpperCase()}
                    </button>
                ))}
            </div>

            <Card className="border-[#123B5D]/10" data-testid="translations-editor-card">
                <CardHeader>
                    <CardTitle className="text-base flex items-center justify-between gap-3">
                        <span>{langs[activeLang]} translation</span>
                        <Badge className={isApproved ? "bg-[#15803D]/10 text-[#15803D]" : "bg-[#D97706]/10 text-[#D97706]"}>
                            {isApproved ? "Approved · live" : "Draft"}
                        </Badge>
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex flex-wrap gap-2">
                        <Button
                            onClick={generate}
                            disabled={generating || isApproved}
                            data-testid="translations-generate"
                        >
                            {generating ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
                            {generating ? " Generating…" : " Generate drafts via AI"}
                        </Button>
                        {isApproved ? (
                            <Button
                                variant="outline"
                                onClick={unapprove}
                                disabled={approving}
                                data-testid="translations-unapprove"
                            >
                                <RotateCcw size={13} /> Move to draft
                            </Button>
                        ) : (
                            <>
                                <Button
                                    variant="outline"
                                    onClick={saveDraft}
                                    disabled={!dirty || saving}
                                    data-testid="translations-save-draft"
                                >
                                    {saving ? "Saving…" : "Save draft"}
                                </Button>
                                <Button
                                    onClick={approve}
                                    disabled={approving}
                                    data-testid="translations-approve"
                                >
                                    <CheckCircle2 size={13} /> {approving ? "Approving…" : "Approve · publish"}
                                </Button>
                            </>
                        )}
                    </div>

                    {row.ai_generated_at ? (
                        <div className="text-xs text-[#17212B]/50">
                            AI drafted on {row.ai_generated_at.split("T")[0]}
                            {row.approved_at ? ` · approved on ${row.approved_at.split("T")[0]}` : ""}
                        </div>
                    ) : null}

                    {FIELD_META.map((f) => (
                        <div key={f.key}>
                            <Label>{f.label}</Label>
                            <Textarea
                                rows={f.rows}
                                maxLength={f.max}
                                value={drafts[f.key] || ""}
                                onChange={onField(f.key)}
                                disabled={isApproved}
                                placeholder={isApproved
                                    ? "Move to draft to edit."
                                    : "Click Generate above to draft this via AI, then edit here."}
                                data-testid={`translations-field-${f.key}`}
                                className={activeLang === "hi" || activeLang === "mr" ? "font-[system-ui]" : ""}
                                style={{ direction: "ltr" }}
                            />
                        </div>
                    ))}

                    <div>
                        <Label>FAQs</Label>
                        <FaqEditor
                            faqs={drafts.faqs}
                            onChange={(next) => { setDrafts({ ...drafts, faqs: next }); setDirty(true); }}
                            disabled={isApproved}
                        />
                    </div>
                </CardContent>
            </Card>
        </DoctorShell>
    );
}
